import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Card from "../shared/Card/Card";
import getAddress from "../../utils/getAddress";
import PriceFormat from "../../utils/priceFormat";
import LoadingWheel from "../shared/LoadingWheel";
import { IAccount } from "../../models/account/IAccount";
import { useAddressLookup } from "../../hooks/useAddressLookup";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useAccountContacts from "../../data/useAccountContacts";
import { AccountCreateEditForm } from "./AccountCreateEditForm";
import { AccountPartProTradeModal } from "./AccountPartProTradeModal";
import useAccountPartProTrade from "../../data/useAccountPartProTrade";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";

export default function AccountDetails({ account }: { account: IAccount }) {
  const { t } = useTranslation("accounts");
  const { accountContacts } = useAccountContacts(account.id, "AccountDetails");
  const { addressLookup, isLoading: addressLookupLoading } = useAddressLookup(account.id);
  const [isEditing, setIsEditing] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  // Enabled, not just present - the API gate fails closed on anything else
  const hasPartProTrade = useFeatureEnabled("PartPro Trade");
  const { link: tradeLink } = useAccountPartProTrade(account.id, hasPartProTrade, "AccountDetails");
  const tradeStatus = tradeLink.data?.data?.status;
  const tradeButtonLabel =
    tradeStatus === "LINKED" || tradeStatus === "REVOKED"
      ? t("PartPro Trade Buyer")
      : tradeStatus === "INVITED"
        ? t("Buyer Invited")
        : t("Invite Buyer");

  const primaryContact = useMemo(
    () =>
      !accountContacts.isLoading && account.primaryContactId
        ? accountContacts.data?.data.find((c) => c.id === account.primaryContactId)
        : null,
    [accountContacts.isLoading, account.primaryContactId]
  );

  const shippingAddress = useMemo(
    () =>
      !addressLookupLoading && account.shippingAddressId
        ? addressLookup(account.shippingAddressId)
        : null,
    [addressLookupLoading, account.shippingAddressId, addressLookup]
  );

  const billingAddress = useMemo(
    () =>
      !addressLookupLoading && account.billingAddressId
        ? addressLookup(account.billingAddressId)
        : null,
    [addressLookupLoading, account.billingAddressId, addressLookup]
  );

  if (isEditing) {
    return (
      <Card title={t("Account Details")} bodyClassName="p-4">
        <AccountCreateEditForm
          account={account}
          onSaved={() => setIsEditing(false)}
          onCancel={() => setIsEditing(false)}
        />
      </Card>
    );
  }

  return (
    <Card title={t("Account Details")} bodyClassName="p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-4">
            {/* left col */}
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Account Number")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {account.accountNumber}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Account Name")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{account.name}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Account Status")}
              </dt>
              <dd className="mt-2 text-sm text-gray-900">
                <span
                  className={`px-3 py-1 rounded-full font-bold ${{
                    0: "bg-amber-100 text-amber-600",
                    1: "bg-red-100 text-red-600",
                    2: "bg-green-100 text-green-600",
                  }[account.status]
                    }`}
                >
                  {
                    {
                      0: t("Archived"),
                      1: t("Disabled"),
                      2: t("Enabled"),
                    }[account.status]
                  }
                </span>
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Primary Contact")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {primaryContact ? (
                  <div className="w-full md:w-1/2 overflow-hidden rounded border bg-gray-100 border-gray-200">
                    <table className="w-full border-collapse">
                      <tbody className="divide-y divide-y-gray-200">
                        <tr className="divide-x">
                          <th className="px-3 py-1 text-right font-medium text-gray-500">
                            {t("Name")}
                          </th>
                          <td width="80%" className="px-3 py-1">
                            {primaryContact?.fullname}
                          </td>
                        </tr>
                        <tr className="divide-x">
                          <th className="px-3 py-1 text-right font-medium text-gray-500">
                            {t("Tel 1")}
                          </th>
                          <td width="80%" className="px-3 py-1">
                            {primaryContact?.telephone1}
                          </td>
                        </tr>
                        <tr className="divide-x">
                          <th className="px-3 py-1 text-right font-medium text-gray-500">
                            {t("Tel 2")}
                          </th>
                          <td width="80%" className="px-3 py-1">
                            {primaryContact?.telephone2}
                          </td>
                        </tr>
                        <tr className="divide-x">
                          <th className="px-3 py-1 text-right font-medium text-gray-500">
                            {t("Email")}
                          </th>
                          <td width="80%" className="px-3 py-1">
                            {primaryContact?.email}
                          </td>
                        </tr>
                        <tr className="divide-x">
                          <th className="px-3 py-1 text-right font-medium text-gray-500">
                            {t("Documents")}
                          </th>
                          <td width="80%" className="px-3 py-1">
                            {primaryContact?.documents.length > 0 ? t("Yes") : t("No")}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <span className="text-gray-600 italic">{t("N/A")}</span>
                )}
              </dd>
            </div>
          </div>
          <div className="space-y-4">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Default Billing Address")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {addressLookupLoading ? (
                  <LoadingWheel />
                ) : billingAddress ? (
                  getAddress(billingAddress)
                ) : (
                  <span className="text-gray-600 italic">{t("N/A")}</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Default Delivery Address")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {addressLookupLoading ? (
                  <LoadingWheel />
                ) : shippingAddress ? (
                  getAddress(shippingAddress)
                ) : (
                  <span className="text-gray-600 italic">{t("N/A")}</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">{t("VAT Number")}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {account.vatNumber ? (
                  account.vatNumber
                ) : (
                  <span className="text-gray-600 italic">{t("N/A")}</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Credit Limit")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {account.creditLimit ? (
                  PriceFormat(account.creditLimit)
                ) : (
                  <span className="text-gray-600 italic">{t("N/A")}</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Sort Code")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {account.bankSortCode ? (
                  `${account.bankSortCode}`
                ) : (
                  <span className="text-gray-600 italic">{t("N/A")}</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Account Number", { context: "bank" })}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {account.bankAccountNumber ? (
                  `${account.bankAccountNumber}`
                ) : (
                  <span className="text-gray-600 italic">{t("N/A")}</span>
                )}
              </dd>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          {hasPartProTrade && (
            <ButtonInput
              isSubmit={false}
              onClick={() => setTradeOpen(true)}
              label={tradeButtonLabel}
              classes=""
            />
          )}
          <ButtonInput
            isSubmit={false}
            onClick={() => setIsEditing(true)}
            label={t("Edit Account")}
            classes=""
          />
        </div>
        {hasPartProTrade && (
          <AccountPartProTradeModal
            accountId={account.id}
            defaultEmail={primaryContact?.email}
            open={tradeOpen}
            setOpen={setTradeOpen}
          />
        )}
      </Card>
  );
}
